import isTruthy from "@/lib/utils/boolean/isTruthy";
import { TZDate } from "@date-fns/tz";
import { Court, Run } from "@shared/lib/database/query.types";
import { format, isToday, isTomorrow, isYesterday, parseISO } from "date-fns";
import { computed } from "vue";

export default function (run: { readonly value: Pick<Run, "start_at" | "end_at"> & { court: Pick<Court, "name" | "timezone"> | null } | null }) {
  const timezone = computed(() => run.value?.court?.timezone || "America/New_York");

  const startDate = computed(() => run.value ? parseISO(run.value.start_at) : null);
  const endDate = computed(() => run.value?.end_at ? parseISO(run.value.end_at) : null);

  const startDateInTimezone = computed(() => {
    if (!startDate.value) return;

    return new TZDate(startDate.value, timezone.value);
  });
  const endDateInTimezone = computed(() => {
    if (!endDate.value) return;

    return new TZDate(endDate.value, timezone.value);
  });

  const formattedDay = computed(() => {
    if (!startDateInTimezone.value) return;

    return format(startDateInTimezone.value, "EEEE");
  });

  const formattedShortDate = computed(() => {
    if (!startDateInTimezone.value) return;

    return format(startDateInTimezone.value, "MMM d");
  });

  const formattedLongDate = computed(() => {
    if (!startDateInTimezone.value) return;

    return format(startDateInTimezone.value, "EEEE, MMMM d, yyyy");
  });

  const formattedRelativeDay = computed(() => {
    if (startDateInTimezone.value && isToday(startDateInTimezone.value)) return "Today";
    if (startDateInTimezone.value && isTomorrow(startDateInTimezone.value)) return "Tomorrow";
    if (startDateInTimezone.value && isYesterday(startDateInTimezone.value)) return "Yesterday";

    return formattedDay.value;
  });

  const formattedStartTime = computed(() => formatTime(startDateInTimezone.value || null));
  const formattedEndTime = computed(() => formatTime(endDateInTimezone.value || null));

  const formattedTimeRange = computed(() => {
    return [formattedStartTime.value, formattedEndTime.value].filter(isTruthy).join(" – ");
  });

  function formatTime (date: Date | null): string | null {
    if (!date) return null;

    return format(date, date.getMinutes() > 0 ? "h:mmaaa" : "haaa");
  }

  return {
    startDateInTimezone,
    formattedShortDate,
    formattedLongDate,
    formattedRelativeDay,
    formattedStartTime,
    formattedEndTime,
    formattedTimeRange,
  };
}
